/**
 * PromptCategoryBadge — small colored badge keyed off
 * `knowledge__prompt.category` for prompt cards. Push #22 rule
 * 3: prompts don't have vendor logos (no vendor), so the category fills
 * the visual-uniqueness slot that vendor logos fill for connectors +
 * models.
 *
 * Palette is deterministic — same category always renders in the same
 * color so the scan pattern is stable across pages. New categories fall
 * back to gray "Other" until the palette is extended.
 *
 * Used on prompt cards in:
 *   - /admin/prompts list
 *   - /prompts list
 *   - PS workshop PromptsWidget assigned rows
 *   - Catalog "Prompts" tab
 *   - Chat rail "Insert prompt" chips (compact mode)
 *
 * Stays OFF the page-header lightning tile — the generic teal squiggle
 * (ObjectIcon kind="prompts") owns that surface per Push #12.
 */

import type { ReactNode } from 'react';

import { formatLabel } from '../lib/format.ts';

interface PromptCategoryBadgeProps {
  category: string | null | undefined;
  size?: 'xs' | 'sm';
}

interface CategoryMeta {
  label: string;
  bg: string;
  fg: string;
}

/**
 * Deterministic palette. Colors are tuned so all 6 sit at the same
 * perceived weight (mid-saturated, ~40% lightness) and don't compete
 * with brand teal/obsidian. "Other" is the gray catch-all for
 * unseeded categories.
 */
const PALETTE: Record<string, CategoryMeta> = {
  sales:        { label: 'Sales',        bg: 'rgba(37, 99, 235, 0.10)',  fg: 'rgb(29, 78, 216)'   }, // blue
  productivity: { label: 'Productivity', bg: 'rgba(124, 58, 237, 0.10)', fg: 'rgb(109, 40, 217)'  }, // purple
  data:         { label: 'Data',         bg: 'rgba(217, 119, 6, 0.10)',  fg: 'rgb(180, 83, 9)'    }, // orange
  support:      { label: 'Support',      bg: 'rgba(22, 163, 74, 0.10)',  fg: 'rgb(21, 128, 61)'   }, // green
  marketing:    { label: 'Marketing',    bg: 'rgba(219, 39, 119, 0.10)', fg: 'rgb(190, 24, 93)'   }, // pink
  engineering:  { label: 'Engineering',  bg: 'rgba(13, 148, 136, 0.10)', fg: 'rgb(15, 118, 110)'  }, // dark teal
  finance:      { label: 'Finance',      bg: 'rgba(180, 83, 9, 0.10)',   fg: 'rgb(146, 64, 14)'   }, // amber
  ops:          { label: 'Ops',          bg: 'rgba(71, 85, 105, 0.10)',  fg: 'rgb(51, 65, 85)'    }, // slate
};

const FALLBACK: CategoryMeta = {
  label: 'Other',
  bg: 'rgba(100, 116, 139, 0.10)',
  fg: 'rgb(71, 85, 105)',
};

function categoryMeta(raw: string | null | undefined): CategoryMeta {
  if (raw === null || raw === undefined || raw === '') return FALLBACK;
  const key = raw.trim().toLowerCase();
  return PALETTE[key] ?? { ...FALLBACK, label: formatLabel(raw) };
}

export function PromptCategoryBadge({ category, size = 'sm' }: PromptCategoryBadgeProps): ReactNode {
  const meta = categoryMeta(category);
  const padY = size === 'xs' ? 2 : 3;
  const padX = size === 'xs' ? 6 : 8;
  const fontSize = size === 'xs' ? 10 : 11;
  return (
    <span
      className="prompt-category-badge"
      style={{
        display: 'inline-block',
        padding: `${padY}px ${padX}px`,
        background: meta.bg,
        color: meta.fg,
        borderRadius: 4,
        fontSize: `${fontSize}px`,
        fontWeight: 600,
        letterSpacing: '0.02em',
        lineHeight: 1.2,
      }}
    >
      {meta.label}
    </span>
  );
}
